import * as React from 'react';
import { Motion, spring } from 'react-motion';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Spinner } from 'components/Elements/Spinner';

const variants = {
  primary:
    'bg-cyan-400/10 text-cyan-200 backdrop-blur-md border border-cyan-300/30 hover:bg-cyan-300/10 shadow-md hover:shadow-lg',
  light:
    'bg-white/10 text-white backdrop-blur-md border border-white/20 hover:bg-white/20',
  outlined:
    'bg-transparent text-white backdrop-blur-md border border-white/30 hover:bg-white/10',
  dark: 'bg-black/30 text-white backdrop-blur-lg border border-white/20 hover:bg-black/40 shadow-md',
  secondary:
    'bg-gradient-to-br from-white/10 to-white/5 text-blue-200 backdrop-blur-md border border-white/20 hover:from-white/20 hover:to-white/10',
  tertiary:
    'bg-transparent text-blue-300 underline hover:text-blue-100 backdrop-blur-sm',
  success:
    'bg-green-400/10 text-green-200 backdrop-blur-md border border-green-300/30 hover:bg-green-300/10',
  danger:
    'bg-red-400/10 text-red-200 backdrop-blur-md border border-red-300/30 hover:bg-red-300/10',
  warning:
    'bg-yellow-400/10 text-yellow-200 backdrop-blur-md border border-yellow-300/30 hover:bg-yellow-300/10',
  gradient:
    'bg-gradient-to-r from-cyan-300/20 to-blue-400/20 text-white backdrop-blur-md border border-white/10 hover:opacity-90',
  glass:
    'bg-white/10 text-white backdrop-blur-md border border-white/20 shadow-md hover:bg-white/20 hover:shadow-lg',
  darkGlass:
    'bg-black/30 text-white backdrop-blur-md border border-white/10 hover:bg-black/40 shadow-md hover:shadow-xl',
  neu: 'bg-neutral-200/10 text-gray-100 backdrop-blur-md border border-white/10 shadow-[0_4px_30px_rgba(0,0,0,0.1)] hover:bg-neutral-300/10',
  glow: 'bg-white/10 backdrop-blur-md text-white border border-white/30 shadow-[0_0_10px_rgba(255,255,255,0.2)] hover:shadow-[0_0_20px_rgba(255,255,255,0.4)]',
};

const sizes = {
  sm: 'py-2 px-4 text-sm',
  md: 'py-2 px-6 text-md',
  lg: 'py-4 px-8 text-lg',
  xl: 'py-5 px-10 text-xl',
};

export const Button = React.forwardRef(
  (
    {
      type = 'button',
      className = '',
      variant = 'gradient',
      size = 'md',
      isLoading = false,
      startIcon,
      endIcon,
      hotkey,
      ...props
    },
    ref
  ) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const [isClicked, setIsClicked] = React.useState(false);

    return (
      <Motion
        style={{
          scale: spring(isHovered ? 1.05 : 1, { stiffness: 170, damping: 20 }),
          pressScale: spring(isClicked ? 0.95 : 1, {
            stiffness: 200,
            damping: 15,
          }),
        }}
      >
        {({ scale, pressScale }) => {
          const finalScale = scale * pressScale;
          return (
            <button
              ref={ref}
              type={type}
              style={{ transform: `scale(${finalScale})` }}
              className={clsx(
                `flex justify-center items-center rounded-2xl font-medium focus:outline-none transition-all duration-300 px-2`,
                sizes[size],
                variants[variant],
                className,
                { 'opacity-70 cursor-not-allowed': props.disabled }
              )}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onMouseDown={() => setIsClicked(true)}
              onMouseUp={() => setIsClicked(false)}
              onTouchStart={() => setIsClicked(true)}
              onTouchEnd={() => setIsClicked(false)}
              {...props}
            >
              {isLoading && <Spinner size="sm" className="text-current" />}
              {!isLoading && startIcon}
              <span className="mx-2">{props.children}</span>
              {!isLoading && endIcon}
              {hotkey && (
                <kbd className="ml-2 px-1.5 py-0.5 text-xs font-semibold bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded">
                  {hotkey}
                </kbd>
              )}
            </button>
          );
        }}
      </Motion>
    );
  }
);

Button.displayName = 'Button';

Button.propTypes = {
  type: PropTypes.string,
  className: PropTypes.string,
  variant: PropTypes.oneOf([
    'light',
    'outlined',
    'dark',
    'primary',
    'secondary',
    'success',
    'glass',
    'neu',
    'darkGlass',
    'danger',
    'warning',
    'tertiary',
  ]),
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  isLoading: PropTypes.bool,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  hotkey: PropTypes.any, // NEW
};
